import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/shared/lib/utils'
import { useDeleteUser } from '@/features/organization-users/hooks/use-organization-users-api'
import type { OrganizationUser } from '@/features/organization-users/types/organization-user'

type OrganizationUserDeleteDialogProps = {
  organizationId: string
  user: OrganizationUser
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function OrganizationUserDeleteDialog({
  organizationId,
  user,
  open,
  onOpenChange,
}: OrganizationUserDeleteDialogProps) {
  const { t } = useTranslation(['organizationUsers', 'common'])
  const { mutateAsync: deleteUser, isPending } = useDeleteUser(organizationId)

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('organizationUsers:deleteDialog.title')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('organizationUsers:deleteDialog.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {t('organizationUsers:deleteDialog.actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            className={cn(buttonVariants({ variant: 'destructive' }))}
            onClick={async (event) => {
              event.preventDefault()
              try {
                await deleteUser(user.id)
                toast.success(t('organizationUsers:deleteDialog.toast.success'))
                onOpenChange(false)
              } catch {
                toast.error(t('organizationUsers:deleteDialog.toast.error'))
              }
            }}
          >
            {t('organizationUsers:deleteDialog.actions.save')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
